import { Duration } from 'luxon'
import { isEqual, orderBy } from 'lodash'

import { ComputedRef } from 'vue'
import { AnalyticsRequest } from './types'
import { AnalyticsField } from './enums'
import { FilterComparison } from '@/components/UI/Filter/utils/enums'
import type { DropdownListData, Filter, FilterParam, RepositoryTimeline, TagClass } from '@types'
import { TagsBunchStore } from '@/store/tags/utils/types'

import { ASSET_FIELD, TAG_FIELD, UNKNOWN_VALUE } from '@/const'
import { TABLE_NAME as ASSETS_TABLE_NAME } from '@/store/assets'
import { TABLE_NAME as TAGS_TABLE_NAME } from '@/store/tags'

import { DuckDBService } from '@/services/duckdb'
import { escapeSqlString, formatSqlIdentifier } from '@/helpers/common'

import { getAnalyticsFilterOptionsConditions, getParamString } from './filterOptions'

export const prepareDatesRangeSlice = (dates: string[], periods?: number) => {
  if (!dates.length || !periods) return []

  if (dates.length > periods) {
    dates = dates.slice(-periods)
  }
  return dates
}

export const convertToSeconds = (millis: number) =>
  Math.ceil(Duration.fromMillis(millis).as('seconds'))

export const compareRequestWithResponse = (
  request: AnalyticsRequest | undefined,
  repositoryId: string | undefined,
  timeline: RepositoryTimeline,
  rootId: string | undefined,
) => {
  if (!request || !repositoryId || !rootId) return false
  const compareRepositoryId = request.rid === repositoryId
  const compareTimelines = isEqual(request.timeline, timeline)
  const compareParams =
    (request.data === 'position' &&
      request.params.accounts === JSON.stringify([rootId])) ||
    request.params.root === rootId
  return compareRepositoryId && compareTimelines && compareParams
}

export const mapTags = (
  tags: string[],
  tagsBunch: Map<string, TagClass>,
  tagsList?: string[],
): Record<string, string> => {
  const result = Object.fromEntries(
    tagsList?.map(tag => [tag, UNKNOWN_VALUE]) || [],
  )
  tags.forEach(tagId => {
    const tag = tagsBunch.get(tagId)
    if (tag) {
      result[tag.field<string>(TAG_FIELD.TAG_NAME).value] = tag.field<string>(
        TAG_FIELD.TAG_VALUE,
      ).value
    }
  })
  return result
}

export const getFilterValues = (
  tableName: string,
  date: ComputedRef<string | undefined>,
  scoped: boolean = false,
  tagsBunchStore?: TagsBunchStore,
) => {  const duckdbInstance = DuckDBService.instance()
  return async (fieldName: string, valueField: string = ASSET_FIELD.NAME, context?: Filter) => {
    const selectedDate = date.value
    if (scoped && !selectedDate) return []
    const narrowing = getAnalyticsFilterOptionsConditions(context, tagsBunchStore?.getMetricTags, ASSETS_TABLE_NAME, TAGS_TABLE_NAME)
    const conditions: string[] = []
    if (scoped) {
      conditions.push(`tn.${AnalyticsField.DATE} = '${escapeSqlString(selectedDate || '')}'`)
    }
    if (narrowing?.where) conditions.push(`(${narrowing.where})`)
    const whereClause = conditions.length ? ` WHERE ${conditions.join(' AND ')}` : ''
    const joinsClause = narrowing?.joins.length ? ` ${narrowing.joins.join(' ')}` : ''
    const sql = `
      SELECT DISTINCT
        ast.${valueField} AS label,
        ast.${valueField} AS value
      FROM ${tableName} as tn
      LEFT JOIN ${ASSETS_TABLE_NAME} ast ON ast.id = tn.${formatSqlIdentifier(fieldName)}${joinsClause}
      ${whereClause}
      ORDER BY label
    `
    const result = await duckdbInstance.query<DropdownListData>(sql)
    return result
  }
}

export const getFilterTags = (
  tableName: string,
  date: ComputedRef<string | undefined>,
  tagsBunchStore: TagsBunchStore,
  scoped: boolean = false,
) => {
  const duckdbInstance = DuckDBService.instance()
  return async (field: string, context?: Filter) => {
    const selectedDate = date.value
    if (scoped && !selectedDate) return []
    const narrowing = getAnalyticsFilterOptionsConditions(context, tagsBunchStore.getMetricTags, ASSETS_TABLE_NAME, TAGS_TABLE_NAME)
    const conditions: string[] = []
    if (scoped) {
      conditions.push(`tn.${AnalyticsField.DATE} = '${escapeSqlString(selectedDate || '')}'`)
    }
    if (narrowing?.where) conditions.push(`(${narrowing.where})`)
    const dateCondition = conditions.join(' AND ')
    const joinsClause = narrowing?.joins.length ? ` ${narrowing.joins.join(' ')}` : ''
    const result = await duckdbInstance.query<{ tag: string }>(
      `SELECT DISTINCT(unnest(tn.${AnalyticsField.TAGS})) as tag FROM ${tableName} as tn${joinsClause}${dateCondition ? ` WHERE ${dateCondition}` : ''}`,
    )
    const tagIds: string[] = []
    const list = result?.reduce((acc, item) => {
      const tagId = item.tag
      const tag = tagsBunchStore.getElementById(tagId)
      const tagName = tag?.field<string>(TAG_FIELD.TAG_NAME).value
      if (tagName === field) {
        const tagValue = tag?.field<string>(TAG_FIELD.TAG_VALUE).value || UNKNOWN_VALUE
        tagValue && acc.push({ value: tagValue, label: tagValue })
        tagIds.push(tagId)
      }
      return acc
    }, [] as DropdownListData[]) || []
    if (tagIds.length) {
      const escapedTagIds = tagIds.map(tagId => tagId.replace(/'/g, '\'\''))
      const arrayLiteral = escapedTagIds.map(id => `'${id}'`).join(',')
      const noValueConditions = [dateCondition, `(tn.${AnalyticsField.TAGS} IS NULL OR NOT (tn.${AnalyticsField.TAGS} && ARRAY[${arrayLiteral}]))`]
        .filter(Boolean)
        .join(' AND ')
      const resultWithoutValue = await duckdbInstance.queryOne<{ count: number }>(
        `SELECT count(*) as count FROM ${tableName} as tn${joinsClause} WHERE ${noValueConditions}`,
      )
      const count = parseInt(resultWithoutValue?.count?.toString() || '0')
      if (count) {
        list?.push({ value: UNKNOWN_VALUE, label: UNKNOWN_VALUE })
      }
    }
    if (!list?.length) {
      list?.push({ value: UNKNOWN_VALUE, label: UNKNOWN_VALUE })
    }
    return orderBy(list, 'label')
  }
}

// duckdb helpers

// choose account or asset table prefix based on field key
const getAnalyticsDuckDbPrefix = (
  key: string,
  isTag = false,
  accountPrefix = 'act.',
  assetPrefix = 'ast.',
  tagPrefix = 'avt.',
  valuePrefix = 'tt.',
  categoryAccountPrefix = 'cact.'
) => isTag
  ? tagPrefix
  : key.startsWith('category_account_')
    ? categoryAccountPrefix
    : key.startsWith('account_')
      ? accountPrefix
      : key.startsWith('asset_')
        ? assetPrefix
        : valuePrefix

const isAnalyticsTagField = (field: string, tagNames?: string[]) => tagNames?.includes(field)

export const getAnalyticsDuckDbFieldName = (tagNames?: string[]) => (field: string) => {
  return isAnalyticsTagField(field, tagNames) ? `"${field}"` : field
}

const getFieldNameWithPrefix = (tagNames?: string[]) => (field: string) => {
  const fieldName = getAnalyticsDuckDbFieldName(tagNames)
  const isTag = isAnalyticsTagField(field, tagNames)
  return `${getAnalyticsDuckDbPrefix(field, isTag, 'tt.', 'tt.', '', 'tt.', 'tt.')}${fieldName(field)}`
}

export const expandDynamicField = (field: string) => {
  switch (field) {
    case AnalyticsField.ACCOUNT_ID:
      return [AnalyticsField.ACCOUNT_ID, AnalyticsField.ACCOUNT_NAME] as string[]
    case AnalyticsField.ASSET_ID:
      return [AnalyticsField.ASSET_ID, AnalyticsField.ASSET_NAME] as string[]
    case AnalyticsField.CATEGORY_ACCOUNT_ID:
      return [AnalyticsField.CATEGORY_ACCOUNT_ID, AnalyticsField.CATEGORY_ACCOUNT_NAME] as string[]
    default:
      return [field]
  }
}

export const isAnalyticsDuckDbDynamicField = (field: string) => {
  return [AnalyticsField.ACCOUNT, AnalyticsField.ASSET, AnalyticsField.CATEGORY_ACCOUNT].includes(field as AnalyticsField)
}

export const prepareAnalyticsDuckDbFilterParam = (param: FilterParam) => {
  if (
    !param.field ||
    !param.comparison ||
    !isAnalyticsDuckDbDynamicField(param.field)
  ) return param
  let field
  if ([FilterComparison.CONTAINS, FilterComparison.NOTCONTAINS].includes(param.comparison)) {
    field = `${param.field}_name`
  } else {
    field = `${param.field}_id`
  }
  return {
    ...param,
    field,
  }
}

export const mapAnalyticsDuckDbFilter = (filter: Filter | undefined, prepareName: ReturnType<typeof getFieldNameWithPrefix>) => {
  if (!filter) return
  const result = filter.params.map(param => {
    if (!param.params) {
      const preparedParam = prepareAnalyticsDuckDbFilterParam(param)
      return getParamString(prepareName)(preparedParam)
    }
    const innerResult = param.params.map(param => {
      const preparedParam = prepareAnalyticsDuckDbFilterParam(param)
      return getParamString(prepareName)(preparedParam)
    })
    return `(${innerResult.join(` ${param.logic} `)})`
  })
  return result.join(` ${filter.logic} `)
}

const getComparedFields = ({
  includeCategoryAccount,
  includeTicker,
  includeTags
}: {
  includeCategoryAccount?: boolean
  includeTicker?: boolean
  includeTags?: boolean
}, prefix?: string) => {
  const result: Record<string, string> = {
    [AnalyticsField.DATE]: `${prefix || 'avt'}.${AnalyticsField.DATE}`,
    [AnalyticsField.ASSET_ID]: `${prefix || 'ast'}.${ASSET_FIELD.ID}`,
    [AnalyticsField.ASSET_NAME]: `${prefix || 'ast'}.${ASSET_FIELD.NAME}`,
    [AnalyticsField.ASSET_TYPE]: `${prefix || 'ast'}.${ASSET_FIELD.TYPE}`,
    [AnalyticsField.ACCOUNT_ID]: `${prefix || 'act'}.${ASSET_FIELD.ID}`,
    [AnalyticsField.ACCOUNT_NAME]: `${prefix || 'act'}.${ASSET_FIELD.NAME}`,
    [AnalyticsField.ACCOUNT_TYPE]: `${prefix || 'act'}.${ASSET_FIELD.TYPE}`,
  }
  if (includeTags) {
    result[AnalyticsField.TAGS] = `array_sort(${prefix || 'avt'}.${AnalyticsField.TAGS})`
  }
  if (includeTicker) {
    result[AnalyticsField.ASSET_TICKER] = `${prefix || 'ast'}.${ASSET_FIELD.TICKER}`
  }
  if (includeCategoryAccount) {
    result[AnalyticsField.CATEGORY_ACCOUNT_ID] = `${prefix || 'cact'}.${ASSET_FIELD.ID}`
    result[AnalyticsField.CATEGORY_ACCOUNT_NAME] = `${prefix || 'cact'}.${ASSET_FIELD.NAME}`
    result[AnalyticsField.CATEGORY_ACCOUNT_TYPE] = `${prefix || 'cact'}.${ASSET_FIELD.TYPE}`
  }
  return result
}

export const getAnalyticsTotalQuerySelect = (tableName: string, staticFilterConditions?: Record<string, string>, posibleValues: string[] = [], splitBySign = false, includeCategoryAccount = false, includeTicker = false) => {
  const result = []
  const comparedFields = getComparedFields({ includeCategoryAccount, includeTicker, includeTags: true })
  // build totals CTE: join assets/accounts and aggregate raw values
  result.push('WITH totals AS (')
  result.push('\tSELECT')
  const selectConditions: string[] = []
  const groupBy: string[] = []
  for (const [key, value] of Object.entries(comparedFields)) {
    selectConditions.push(`\t\t${value} AS ${key}`)
  }
  for (const value of posibleValues) {
    selectConditions.push(`\t\tSUM(${value}) AS ${value}`)
  }
  if (splitBySign) {
    for (const value of posibleValues) {
      selectConditions.push(`\t\t(CASE WHEN ${value} >= 0 THEN 1 ELSE 0 END) AS ${value}_positive`)
      groupBy.push(`${value}_positive`)
    }
  }
  result.push(selectConditions.join(',\n'))
  result.push(`\tFROM ${tableName} avt`)
  result.push(`\tLEFT JOIN ${ASSETS_TABLE_NAME} act ON act.${ASSET_FIELD.ID} = avt.${AnalyticsField.ACCOUNT_ID}`)
  result.push(`\tLEFT JOIN ${ASSETS_TABLE_NAME} ast ON ast.${ASSET_FIELD.ID} = avt.${AnalyticsField.ASSET_ID}`)
  if (includeCategoryAccount) {
    result.push(`\tLEFT JOIN ${ASSETS_TABLE_NAME} cact ON cact.${ASSET_FIELD.ID} = avt.${AnalyticsField.CATEGORY_ACCOUNT_ID}`)
  }
  // apply optional static filters
  const staticFilterConditionEntries = staticFilterConditions && Object.entries(staticFilterConditions)
  if (staticFilterConditionEntries?.length) {
    const filterConditions = staticFilterConditionEntries.map(([key, value]) => `${key} = '${escapeSqlString(value)}'`)
    result.push(`\tWHERE ${filterConditions.join(' AND ')}`)
  }
  // group by account/asset fields and raw tag array
  for (const key of Object.keys(comparedFields)) {
    groupBy.push(comparedFields[key as keyof typeof comparedFields])
  }
  result.push(`\tGROUP BY ${groupBy.join(', ')}`)
  result.push(')')
  return result
}

export const getAnalyticsBaseQuerySelect = (filter?: Filter, tagNames?: string[], posibleValues: string[] = [], preSetFilterConditions: string[] = [], splitBySign = false, includeCategoryAccount = false, includeTicker = false) => {
  const result = []
  const comparedFields = getComparedFields({ includeCategoryAccount, includeTicker }, 'tt.')
  // build base CTE: pivot tag names into columns and keep value aggregates
  result.push('base AS (')
  result.push('\tSELECT')
  const selectConditions: string[] = []
  const groupBy: string[] = []
  for (const key of Object.keys(comparedFields)) {
    selectConditions.push(`\t\t${key}`)
  }
  // keep aggregated values from totals
  for (const value of posibleValues) {
    selectConditions.push(`\t\ttt.${value}`)
    groupBy.push(`tt.${value}`)
    if (splitBySign) {
      selectConditions.push(`\t\ttt.${value}_positive`)
      groupBy.push(`tt.${value}_positive`)
    }
  }
  // add tag columns (one per tag name) with UNKNOWN fallback
  for (const row of tagNames || []) {
    selectConditions.push(`\t\tCOALESCE(MAX(CASE WHEN tg.${TAG_FIELD.TAG_NAME} = '${escapeSqlString(row)}' THEN tg.${TAG_FIELD.TAG_VALUE} END), '${UNKNOWN_VALUE}') AS "${escapeSqlString(row)}"`)

  }
  result.push(selectConditions.join(',\n'))
  result.push('\tFROM totals as tt')
  // join tags only when tag columns are requested
  if (tagNames?.length) {
    result.push('\tLEFT JOIN UNNEST(tt.tags) AS u(tag_id) ON TRUE')
    result.push(`\tLEFT JOIN ${TAGS_TABLE_NAME} tg ON tg.${TAG_FIELD.ID} = u.tag_id AND tg.${TAG_FIELD.TAG_NAME} IN (${tagNames?.map(row => `'${escapeSqlString(row)}'`).join(', ')})`)
  }
  // group by all non-aggregated columns in base
  for (const key of Object.keys(comparedFields)) {
    groupBy.push(key)
  }
  result.push(`\tGROUP BY ${groupBy.join(', ')}`)
  // apply dynamic filters
  const filterConditions: string[] = preSetFilterConditions
  const dynamicConditions = mapAnalyticsDuckDbFilter(filter, getFieldNameWithPrefix(tagNames))
  if (dynamicConditions) {
    filterConditions.push(dynamicConditions)
  }
  // apply filters
  if (filterConditions.length) {
    result.push(`\tHAVING ${filterConditions.join(' AND ')}`)
  }
  result.push(')')
  return result
}

export const getAnalyticsSummarySelect = (amountField: string) => {
  const result = []
  result.push('summary AS (')
  result.push('\tSELECT')
  result.push('\t\tCOUNT(*) AS total,')
  result.push(`\t\tSUM(${amountField}) AS sum,`)
  result.push(
    `\t\tSUM(CASE WHEN ${amountField} >= 0 THEN ${amountField} ELSE 0 END) AS positive_sum,`,
  )
  result.push(
    `\t\tSUM(CASE WHEN ${amountField} < 0 THEN ${amountField} ELSE 0 END) AS negative_sum,`,
  )
  result.push('\tFROM result')
  result.push(')')
  return result
}
